// import core components
import dynamic from "next/dynamic";
import { useEffect } from "react";
import Image from "next/image";
import Slider from "react-slick";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";

// import custom components
import SEO from "@/elements/Shared/SEO";
import Hero from "@/elements/Hero";
const WorkTogether = dynamic(() => import("@/elements/WorkTogether"));
const Quote = dynamic(() => import("@/elements/Quote"));
const StepCard = dynamic(() => import("@/elements/Shared/StepCard"));
const OtherServices = dynamic(() => import("@/elements/Shared/OtherServices"));
const BlockTextImage = dynamic(
  () => import("@/elements/Shared/BlockTextImage")
);
const WhiteSquare = dynamic(
  () => import("@/elements/Shared/DesignComponents/WhiteSquare")
);
const Circle = dynamic(
  () => import("@/elements/Shared/DesignComponents/Circle")
);
const DegradeEffect = dynamic(
  () => import("@/elements/Shared/DesignComponents/DegradeEffect")
);

// import styles
import styles from "./campagnesMarketingWeb.module.scss";

// import animation
import {
  fiveStepAnimations,
  platformAnimation,
  strategyAnimation,
} from "@/utils/pages/campagne-marketing";

// interfaces
import MenuOptions from "@/interfaces/MenuOptions";

// import data
import CMW from "@/data/pages/services/campagnes_marketing_web.json";

type Props = {
  menuOptions: MenuOptions;
};

export default function CampagnesMarketingWeb({ menuOptions }: Props) {
  const { seo, hero, platforms, personas, our_5_steps, strategies, marketing } =
    CMW;

  const settings = {
    dots: true,
    arrows: false,
    infinite: false,
    speed: 500,
    slidesToScroll: 1,
    slidesToShow: 2,
    swipeToSlide: true,
    adaptiveHeight: false,
    responsive: [
      { breakpoint: 767, settings: { slidesToShow: 1 } },
      { breakpoint: 1023, settings: { slidesToShow: 2 } },
      { breakpoint: 10000000, settings: "unslick" },
    ],
  };

  if (process.browser) {
    ScrollTrigger.getAll().forEach((t) => {
      t.kill();
    });
  }

  const divUnderBlockText = () => {
    return (
      <div className={styles["main-platforms--logo"]}>
        <div className={`platform-logo-text ${styles["google-ads"]}`}>
          <Image
            src="/icons/logos/logo-google-partner.png"
            alt="Logo google"
            width={200}
            height={74}
            layout="responsive"
          />
        </div>
        <div className={`platform-logo-text ${styles["facebook-certified"]}`}>
          <Image
            src="/icons/logos/logo-facebook-certified-media-buying-professional.png"
            alt="Logo facebook certified"
            width={1}
            height={1}
            layout="responsive"
          />
        </div>
      </div>
    );
  };

  useEffect(() => {
    menuOptions.setMenuColor("black");
  });

  return (
    <>
      <SEO seo={seo} />
      <main className={styles["main"]}>
        <Hero
          isSquareDesignVisible={true}
          data={hero}
          dispositionContainer={5}
          pageName="cmw"
          heroId="hero--light-bg"
        />

        <section className={styles["platforms"]}>
          <BlockTextImage
            title={platforms.title}
            content={platforms.content}
            images={platforms.images}
            HtagLevel={2}
            isImageAllEgal={true}
            divUnderBlockText={divUnderBlockText()}
            dispositionContainer={15}
            sectionName="platforms"
            canAnimationBePaused={true}
            optionsAnimImages={{
              type: "popUp&FadeInRotate",
              rotationOptions: {
                turnQty: 2,
                minute: 10,
              },
            }}
            externalAnimations={{
              externalAnimation: platformAnimation,
              addTo: "textTl",
            }}
          />
          <Circle
            circle={{
              isAlternativeColor: false,
              isVisibleOnMobile: true,
              isAllSizeEgal: false,

              borderSize: "small",
              colorStyle: 5,
              styleNumber: 1,
            }}
            linearGradientId="campagnes-marketing-web-1"
            pathId="campagnes-marketing-path-1"
            circleId="campagnes-marketing-circle-1"
          />

          <Circle
            circle={{
              isSvg: true,
              isAlternativeColor: false,
              isVisibleOnMobile: true,
              isAllSizeEgal: false,

              borderSize: "small",
              colorStyle: 5,
              styleNumber: 1,
            }}
            linearGradientId="campagnes-marketing-web-2"
            pathId="campagnes-marketing-path-2"
            circleId="campagnes-marketing-circle-2"
          />
        </section>

        <Quote />

        <section className={styles["personas"]}>
          <BlockTextImage
            title={personas.title}
            content={personas.content}
            images={personas.images}
            circles={personas.circles}
            HtagLevel={2}
            isImageAllEgal={true}
            subtitleFontSizeStyle="small"
            dispositionContainer={1}
            sectionName="personas"
            optionsAnimImages={{
              type: "fadeScaleXscale",
              firstImageToAnimate: 1,
              lastImageToAnimate: 2,
              overlay: {
                isPresent: true,
                imageNumber: 1,
              },
              transformOriginOrder: ["center", "left 75%"],
            }}
          />
          <WhiteSquare
            position="top-left"
            isDefaultColor={false}
            isLighter={true}
            isAlternative={true}
          />
        </section>

        <section className={styles["our-step"]}>
          <BlockTextImage
            title={our_5_steps.title}
            content={our_5_steps.content}
            button={our_5_steps.button}
            HtagLevel={2}
            isImageAllEgal={true}
            dispositionContainer={16}
            sectionName="steps"
            externalAnimations={{
              externalAnimation: fiveStepAnimations,
              addTo: "none",
            }}
          >
            <Slider {...settings} className={styles["our-steps--containers"]}>
              {our_5_steps.steps.map((step, i) => {
                return (
                  <StepCard
                    key={step.title + i}
                    className={`five-step-card ${styles["our-step--card"]}`}
                    stepNumber={i + 1}
                    stepId={styles[`cmw-step-${i + 1}`]}
                    noMargin={true}
                    title={step.title}
                    text={step.content}
                    showDots={true}
                  />
                );
              })}
            </Slider>
          </BlockTextImage>

          <Circle
            circle={{
              isAlternativeColor: false,
              isVisibleOnMobile: true,
              isAllSizeEgal: false,
              borderSize: "small",
              colorStyle: 5,
              styleNumber: 2,
            }}
            linearGradientId="campagnes-marketing-web-3"
            pathId="campagnes-marketing-path-3"
            circleId="campagnes-marketing-circle-3"
          />

          <Circle
            circle={{
              isSvg: true,
              isAlternativeColor: false,
              isVisibleOnMobile: true,
              isAllSizeEgal: false,

              borderSize: "small",
              colorStyle: 2,
              styleNumber: 2,
            }}
            linearGradientId="campagnes-marketing-web-4"
            pathId="campagnes-marketing-path-4"
            circleId="campagnes-marketing-circle-4"
          />

          <Circle
            circle={{
              isAlternativeColor: false,
              isVisibleOnMobile: true,
              isAllSizeEgal: false,

              borderSize: "small",
              colorStyle: 5,
              styleNumber: 2,
            }}
            linearGradientId="campagnes-marketing-web-5"
            pathId="campagnes-marketing-path-5"
            circleId="campagnes-marketing-circle-5"
          />

          <Circle
            circle={{
              isSvg: true,
              isAlternativeColor: false,
              isVisibleOnMobile: true,
              isAllSizeEgal: false,

              borderSize: "small",
              colorStyle: 2,
              styleNumber: 2,
            }}
            pathId="campagnes-marketing-path-6"
            circleId="campagnes-marketing-circle-6"
            linearGradientId="campagnes-marketing-web-6"
          />
        </section>

        <section className={styles["strategies"]}>
          <BlockTextImage
            title={strategies.title}
            content={strategies.content}
            HtagLevel={2}
            dispositionContainer={17}
            sectionName="strategies"
            externalAnimations={{
              externalAnimation: strategyAnimation,
              addTo: "none",
            }}
          >
            <div className={styles["strategies--container"]}>
              {strategies.steps.map((step, i) => {
                const { icon, alt, title, width, height } = step;
                return (
                  <StepCard
                    key={title + i}
                    title={title}
                    stepId={`strategies-step-${i + 1}`}
                    image={{ icon, alt, width, height }}
                    className={`strat-card ${styles["strategies--step"]}`}
                  />
                );
              })}
            </div>
          </BlockTextImage>
          <DegradeEffect size={1112.34} position={5} />
          <Circle
            circle={{
              isAlternativeColor: false,
              isVisibleOnMobile: true,
              isAllSizeEgal: false,
              borderSize: "small",
              colorStyle: 5,
              styleNumber: 3,
            }}
            pathId="campagnes-marketing-path-7"
            circleId="campagnes-marketing-circle-7"
            linearGradientId="campagnes-marketing-web-7"
          />

          <Circle
            circle={{
              isSvg: true,
              isAlternativeColor: false,
              isVisibleOnMobile: true,
              isAllSizeEgal: false,

              borderSize: "small",
              colorStyle: 2,
              styleNumber: 3,
            }}
            pathId="campagnes-marketing-path-8"
            circleId="campagnes-marketing-circle-8"
            linearGradientId="campagnes-marketing-web-8"
          />
          <Circle
            circle={{
              isSvg: true,
              isAlternativeColor: false,
              isVisibleOnMobile: true,
              isAllSizeEgal: false,

              borderSize: "small",
              colorStyle: 5,
              styleNumber: 3,
            }}
            pathId="campagnes-marketing-path-9"
            circleId="campagnes-marketing-circle-9"
            linearGradientId="campagnes-marketing-web-9"
          />
        </section>

        <section className={styles["marketing"]}>
          <BlockTextImage
            content={marketing.content}
            title={marketing.title}
            images={marketing.images}
            circles={marketing.circles}
            button={marketing.button}
            HtagLevel={2}
            subtitleFontSizeStyle="medium"
            dispositionContainer={18}
            sectionName="marketing"
            optionsAnimImages={{
              type: "fadeMotionPath",
              duration: 1,
              alignsOrder: [
                [0.75, 0.25],
                [0.5, 0.75],
              ],
              svgPathOrder: [2, 2],
              startOrder: [0.4, 0.18],
              endOrder: [0.46, 0.22],
            }}
          />
          <Circle
            circle={{
              isSvg: true,
              isAlternativeColor: false,
              isVisibleOnMobile: true,
              isAllSizeEgal: false,
              borderSize: "small",
              colorStyle: 5,
              styleNumber: 4,
            }}
            pathId="campagnes-marketing-path-10"
            circleId="campagnes-marketing-circle-10"
            linearGradientId="campagnes-marketing-web-10"
          />

          <Circle
            circle={{
              isSvg: true,
              isAlternativeColor: false,
              isVisibleOnMobile: true,
              isAllSizeEgal: false,

              borderSize: "small",
              colorStyle: 5,
              styleNumber: 4,
            }}
            pathId="campagnes-marketing-path-11"
            circleId="campagnes-marketing-circle-11"
            linearGradientId="campagnes-marketing-web-11"
          />
        </section>
        <OtherServices currentServiceName="Campagnes marketing web" />
        <WorkTogether
          content="Vous recherchez des experts qui <strong>s’engagent à fond dans votre réussite</strong>? Faites-nous part de votre projet et nous bâtirons des solutions à la mesure de vos ambitions."
          isDesignVisible={false}
        />
      </main>
    </>
  );
}
